// Lets a floating button be dragged anywhere on screen (mouse, touch or pen), remembers where it was left,
// and moves with Shift + arrow keys for keyboard users. A drag never counts as a click.
import { useCallback, useEffect, useRef, useState } from "react";

const SIZE = 44;
const EDGE = 8;

function clamp(p) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return { x: Math.min(Math.max(EDGE, p.x), w - SIZE - EDGE), y: Math.min(Math.max(EDGE, p.y), h - SIZE - EDGE) };
}

function load(key) {
  try {
    const v = JSON.parse(localStorage.getItem(key) || "null");
    if (v && typeof v.fx === "number" && typeof v.fy === "number") {
      return clamp({ x: v.fx * window.innerWidth, y: v.fy * window.innerHeight });
    }
  } catch { /* storage blocked */ }
  return null;
}

function save(key, p) {
  try { localStorage.setItem(key, JSON.stringify({ fx: p.x / window.innerWidth, fy: p.y / window.innerHeight })); } catch { /* storage blocked */ }
}

export function useDraggableFab(key = "fable_a11y_pos") {
  const [pos, setPos] = useState(() => (typeof window === "undefined" ? null : load(key)));
  const drag = useRef(null);
  const moved = useRef(false);

  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clamp(p) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = useCallback((e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const r = e.currentTarget.getBoundingClientRect();
    drag.current = { id: e.pointerId, dx: e.clientX - r.left, dy: e.clientY - r.top, sx: e.clientX, sy: e.clientY };
    moved.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    if (!moved.current && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 6) return;
    moved.current = true;
    setPos(clamp({ x: e.clientX - d.dx, y: e.clientY - d.dy }));
  }, []);

  const onPointerUp = useCallback((e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (moved.current) setPos((p) => { if (p) save(key, p); return p; });
  }, [key]);

  // Swallow the click that ends a drag so the panel doesn't open.
  const onClickCapture = useCallback((e) => {
    if (moved.current) { e.preventDefault(); e.stopPropagation(); moved.current = false; }
  }, []);

  const onKeyDown = useCallback((e) => {
    if (!e.shiftKey || !e.key.startsWith("Arrow")) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const step = 24;
    const base = pos || { x: r.left, y: r.top };
    const next = clamp({
      x: base.x + (e.key === "ArrowRight" ? step : e.key === "ArrowLeft" ? -step : 0),
      y: base.y + (e.key === "ArrowDown" ? step : e.key === "ArrowUp" ? -step : 0),
    });
    setPos(next);
    save(key, next);
  }, [pos, key]);

  const reset = useCallback(() => {
    setPos(null);
    try { localStorage.removeItem(key); } catch { /* storage blocked */ }
  }, [key]);

  const style = pos ? { left: pos.x, top: pos.y, bottom: "auto", right: "auto", touchAction: "none" } : { touchAction: "none" };
  return { style, moved: !!pos, reset, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onClickCapture, onKeyDown } };
}
